import React, { useEffect, useState } from "react";
import { Modal, Button, Row, Col, Container, Form, Spinner } from "react-bootstrap";
import { Formik, Field } from "formik";
import * as Yup from "yup";
import { useAppSelector, useAppDispatch } from "../../../../store/hooks";
import {
	createUserAsync,
	getAllUsersAsync,
} from "../../../../slices/user/userSlice";
import { getAllDropdownRolesAsync } from "../../../../slices/settings/settingsSlice";
import { getAllDropdownProjectsAsync } from "../../../../slices/project/projectSlice";
import { RootState } from "../../../../store/store";
import MultipleSelect from "../../../../components/form/custom-select";
import { Select, FormControl, InputLabel, MenuItem } from "@mui/material";
import { toast } from "react-toastify";
import { handlePhoneNumberChange } from "util/inputUtils";

interface CreateTeamValues {
	company_id: string;
	first_name: string;
	last_name: string;
	email: string;
	phone_number: string;
	role_id: string;
	projects: string[];
	status_id: number;
}

interface CreateTeamProps {
	closeModal: () => void;
	show: boolean;
	onCreateSuccess: () => void;
}

const CreateTeam: React.FC<CreateTeamProps> = ({
	closeModal,
	show,
	onCreateSuccess,
}) => {
	const dispatch = useAppDispatch();
	const companyId = useAppSelector(
		(state: RootState) => state.auth.user?.company_id || "",
	);
	const roles = useAppSelector((state) => state.settings.roles?.data);
	const projects = useAppSelector((state) => state.project.projects?.data);
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const [pageNumber, setPage] = useState(1);

	useEffect(() => {
		if (companyId) {
			dispatch(getAllDropdownRolesAsync({ companyId }));
		}
	}, [dispatch, companyId]);
	useEffect(() => {
		if (companyId) {
			dispatch(getAllDropdownProjectsAsync({ companyId }));
		}
	}, [dispatch, companyId]);

	console.log("Roles:", roles);
	console.log("DropdownProjects:", projects);

	return (
		<Container fluid className="">
			<Modal
				centered
				size="lg"
				show={show}
				onHide={closeModal}
				style={{ maxHeight: "100vh", overflowY: "auto" }}
			>
				<div className="px-5">
					<Formik
						initialValues={{
							company_id: companyId || "",
							first_name: "",
							last_name: "",
							email: "",
							phone_number: "",
							role_id: "",
							projects: [],
							status_id: 1,
						}}
						validationSchema={Yup.object().shape({
							first_name: Yup.string().required("Required"),
							last_name: Yup.string().required("Required"),
							email: Yup.string()
								.email("Invalid email")
								.required("Email is required"),
							phone_number: Yup.string()
								.matches(
									/^(070|080|081|090|091)[0-9]{8}$/,
									"Please enter a valid 11 digit phone number in Nigerian format e.g 08012345678",
								)
								.required("Please enter a valid 11 digit phone number"),
							role_id: Yup.string().required("Required"),
							projects: Yup.array()
								.of(Yup.string())
								.min(1, "At least one project is required")
								.required("Required"),
						})}
						onSubmit={(values: CreateTeamValues, { setSubmitting }) => {
							console.log("Submitting values:", values);
							dispatch(
								createUserAsync({
									...values,
									company_id: companyId || "",
								}),
							)
								.unwrap()
								.then((response) => {
									toast.success("Member created successfully");
									dispatch(getAllUsersAsync({ companyId, page: pageNumber }));
									onCreateSuccess();
									closeModal();
								})
								.catch((error) => {
									const errorMessage = Object.values(error.message)
										.flat()
										.join(" ");
									toast.error(errorMessage);
								})
								.finally(() => {
									setSubmitting(false);
								});
						}}
					>
						{({
							errors,
							touched,
							handleSubmit,
							isSubmitting,
							values,
							handleChange,
							setFieldValue,
						}) => (
							<Form onSubmit={handleSubmit}>
								<Modal.Header closeButton className="text-start mt-2">
									<h4 className="confirm-header">Add new member</h4>
								</Modal.Header>
								<Modal.Body>
									<Row>
										<Col md={6} className="mt-2">
											<label className="mt-2">
												<p>
													First name <span className="text-danger">*</span>
												</p>
											</label>
											<Field
												type="text"
												name="first_name"
												placeholder="Enter first name"
												className={`fluent-input ${touched.first_name && errors.first_name
													? "is-invalid"
													: ""
													}`}
												required
											/>
											<div className="invalid-feedback d-block">
												{touched.first_name && errors.first_name}
											</div>
										</Col>
										<Col md={6} className="mt-2">
											<label className="mt-2">
												<p>
													Last name <span className="text-danger">*</span>
												</p>
											</label>
											<Field
												type="text"
												name="last_name"
												placeholder="Enter last name"
												className={`fluent-input ${touched.last_name && errors.last_name
													? "is-invalid"
													: ""
													}`}
												required
											/>
											<div className="invalid-feedback d-block">
												{touched.last_name && errors.last_name}
											</div>
										</Col>
									</Row>
									<Row>
										<Col md={6} className="mt-2">
											<label className="mt-2">
												<p>
													Email address <span className="text-danger">*</span>
												</p>
											</label>
											<Field
												type="email"
												name="email"
												placeholder="Enter email address"
												className={`fluent-input ${touched.email && errors.email ? "is-invalid" : ""
													}`}
												required
											/>
											<div className="invalid-feedback d-block">
												{touched.email && errors.email}
											</div>
										</Col>
										<Col md={6} className="mt-2">
											<label className="mt-2">
												<p>
													Phone Number <span className="text-danger">*</span>
												</p>
											</label>
											<Field
												type="text"
												name="phone_number"
												placeholder="Enter phone number"
												className={`fluent-input ${touched.phone_number && errors.phone_number
													? "is-invalid"
													: ""
													}`}
												onInput={handlePhoneNumberChange}
												required
											/>
											<div className="invalid-feedback d-block">
												{touched.phone_number && errors.phone_number}
											</div>
										</Col>
									</Row>
									<Row>
										<Col md={6} className="mt-2">
											<label className="mt-2">
												<p>
													Role <span className="text-danger">*</span>
												</p>
											</label>
											<FormControl fullWidth>
												<InputLabel>Select role</InputLabel>
												<Select
													label="Select role"
													name="role_id"
													value={values.role_id}
													onChange={handleChange}
												>
													{roles?.map((role) => (
														<MenuItem key={role.id} value={role.id}>
															{role.role_name}
														</MenuItem>
													))}
												</Select>
											</FormControl>
											<div className="invalid-feedback d-block">
												{touched.role_id && errors.role_id}
											</div>
										</Col>
										<Col md={6} className="mt-2">
											<label className="mt-2">
												<p>
													Assign project <span className="text-danger">*</span>
												</p>
											</label>
											<MultipleSelect
												label="Select project(s)"
												name="projects"
												options={
													projects
														? projects.map((project: any) => ({
															value: project.id,
															label: project.project_name,
														}))
														: [] // Default to an empty array if projects is undefined
												}
												value={values.projects}
												onChange={(selectedProjects: string[]) =>
													setFieldValue("projects", selectedProjects)
												}
											/>

											<div className="invalid-feedback d-block">
												{touched.projects && errors.projects}
											</div>
										</Col>
									</Row>
									<Modal.Footer className="mt-5">
										<Row>
											<Col md={6}>
												<div className="mt-1">
													<Button onClick={closeModal} className="cancel-btn">
														<span className="text-center">Cancel</span>
													</Button>
												</div>
											</Col>
											<Col md={6}>
												<div className="mt-1">
													<Button
														type="submit"
														className="border-0 create-btn bg-color-1"
													>
														{isSubmitting ? (
															<Spinner animation="border" size="sm" />
														) : (
															<span className="text-center">Create member</span>
														)}
													</Button>
												</div>
											</Col>
										</Row>
									</Modal.Footer>
								</Modal.Body>
							</Form>
						)}
					</Formik>
				</div>
			</Modal>
		</Container>
	);
};

export default CreateTeam;
